import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/Alert';
import {
  CreditCard, 
  Search, 
  Download, 
  Eye, 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  DollarSign, 
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('30days');
  const [sortBy, setSortBy] = useState('oldest');

  const mockPayments = [
    {
      id: 'PAY-001',
      transactionId: 'txn_abc123def456',
      customer: 'Acme Corporation',
      customerEmail: 'billing@acme.com',
      amount: 299,
      status: 'completed',
      paymentMethod: 'credit_card',
      cardLast4: '4242',
      cardBrand: 'visa',
      invoiceId: 'INV-2023-001',
      subscription: 'Enterprise Pro',
      processedAt: '2023-10-14T10:30:00Z',
      createdAt: '2023-10-14T10:25:00Z',
      failureReason: null,
      refundAmount: 0,
      refundReason: null,
      refundedAt: null,
      processingFee: 8.97,
      netAmount: 290.03,
      currency: 'USD',
      gateway: 'stripe',
      lastModified: '2023-10-14'
    },
    {
      id: 'PAY-002',
      transactionId: 'txn_xyz789abc123',
      customer: 'TechStart Inc',
      customerEmail: 'finance@techstart.com',
      amount: 99,
      status: 'completed',
      paymentMethod: 'credit_card',
      cardLast4: '5555',
      cardBrand: 'mastercard',
      invoiceId: 'INV-2023-002',
      subscription: 'Professional',
      processedAt: '2023-10-19T14:15:00Z',
      createdAt: '2023-10-19T14:10:00Z',
      failureReason: null,
      refundAmount: 0,
      refundReason: null,
      refundedAt: null,
      processingFee: 2.97,
      netAmount: 96.03,
      currency: 'USD',
      gateway: 'stripe',
      lastModified: '2023-10-19'
    },
    {
      id: 'PAY-003',
      transactionId: 'txn_def456ghi789',
      customer: 'Global Solutions',
      customerEmail: 'accounts@globalsolutions.com',
      amount: 199,
      status: 'pending',
      paymentMethod: 'bank_transfer',
      cardLast4: null,
      cardBrand: null,
      invoiceId: 'INV-2023-003',
      subscription: 'Enterprise',
      processedAt: null,
      createdAt: '2023-10-25T09:00:00Z',
      failureReason: null,
      refundAmount: 0,
      refundReason: null,
      refundedAt: null,
      processingFee: 0,
      netAmount: 199,
      currency: 'USD',
      gateway: 'bank_transfer',
      lastModified: '2023-10-25'
    },
    {
      id: 'PAY-004',
      transactionId: 'txn_ghi789jkl012',
      customer: 'Small Business Co',
      customerEmail: 'admin@smallbiz.com',
      amount: 29,
      status: 'failed',
      paymentMethod: 'credit_card',
      cardLast4: '1234',
      cardBrand: 'visa',
      invoiceId: 'INV-2023-004',
      subscription: 'Starter',
      processedAt: null,
      createdAt: '2023-10-10T16:45:00Z',
      failureReason: 'Insufficient funds',
      refundAmount: 0,
      refundReason: null,
      refundedAt: null,
      processingFee: 0,
      netAmount: 0,
      currency: 'USD',
      gateway: 'stripe',
      lastModified: '2023-10-10'
    },
    {
      id: 'PAY-005',
      transactionId: 'txn_jkl012mno345',
      customer: 'Enterprise Corp',
      customerEmail: 'finance@enterprise.com',
      amount: 299,
      status: 'refunded',
      paymentMethod: 'credit_card',
      cardLast4: '8888',
      cardBrand: 'amex',
      invoiceId: 'INV-2023-006',
      subscription: 'Enterprise Pro',
      processedAt: '2023-09-15T11:20:00Z',
      createdAt: '2023-09-15T11:15:00Z',
      failureReason: null,
      refundAmount: 299,
      refundReason: 'Customer requested cancellation',
      refundedAt: '2023-09-20T14:30:00Z',
      processingFee: 8.97,
      netAmount: -299,
      currency: 'USD',
      gateway: 'stripe',
      lastModified: '2023-09-20'
    },
    {
      id: 'PAY-006',
      transactionId: 'txn_mno345pqr678',
      customer: 'New Customer LLC',
      customerEmail: 'billing@newcustomer.com',
      amount: 500,
      status: 'completed',
      paymentMethod: 'bank_transfer',
      cardLast4: null,
      cardBrand: null,
      invoiceId: 'INV-2023-005',
      subscription: 'Enterprise Pro',
      processedAt: '2023-10-30T13:00:00Z',
      createdAt: '2023-10-30T12:55:00Z',
      failureReason: null,
      refundAmount: 0,
      refundReason: null,
      refundedAt: null,
      processingFee: 0,
      netAmount: 500,
      currency: 'USD',
      gateway: 'bank_transfer',
      lastModified: '2023-10-30'
    }
  ];

  const filteredPayments = payments
    .filter(payment =>
      payment.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.invoiceId.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(payment => filterStatus === 'all' || payment.status === filterStatus)
    .filter(payment => filterMethod === 'all' || payment.paymentMethod === filterMethod)
    .sort((a, b) => {
      if (sortBy === 'oldest') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      if (sortBy === 'amount-high') {
        return b.amount - a.amount;
      }
      if (sortBy === 'amount-low') {
        return a.amount - b.amount;
      }
      // Default to oldest if no sort selected
      return new Date(a.createdAt) - new Date(b.createdAt);
    });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return <Badge variant="success">Completed</Badge>;
      case 'pending': return <Badge variant="warning">Pending</Badge>;
      case 'failed': return <Badge variant="destructive">Failed</Badge>;
      case 'refunded': return <Badge variant="secondary">Refunded</Badge>;
      case 'cancelled': return <Badge variant="secondary">Cancelled</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'credit_card': return <CreditCard className="h-4 w-4" />;
      case 'bank_transfer': return <FileText className="h-4 w-4" />;
      case 'paypal': return <DollarSign className="h-4 w-4" />;
      default: return <DollarSign className="h-4 w-4" />;
    }
  };

  const getCardBrandColor = (brand) => {
    switch (brand) {
      case 'visa': return 'text-blue-600';
      case 'mastercard': return 'text-red-600';
      case 'amex': return 'text-green-600';
      case 'discover': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const loadPayments = useCallback(async (showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Simulate API call - in real app, filterPeriod would be used to filter by date range
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setPayments(mockPayments);
      
      if (showToast) {
        toast.success('Transactions refreshed successfully');
      }
    } catch (err) {
      if (showToast) {
        toast.error('Failed to refresh transactions');
      }
      console.error('Error loading payments:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterPeriod]); // filterPeriod is included for future date filtering functionality

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleRetryPayment = (paymentId) => {
    // In a real app, this would retry the failed payment
    //console.log('Retrying payment:', paymentId);
  };

  const handleRefundPayment = (paymentId) => {
    // In a real app, this would initiate a refund
    //console.log('Refunding payment:', paymentId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Transactions</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => loadPayments(true)}
            disabled={loading || refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${(loading || refreshing) ? 'animate-spin' : ''}`} /> 
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button variant="outline" className="flex items-center gap-2">
            <Download className="h-4 w-4" /> Export
          </Button>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-700 dark:text-green-300">Successful Payments</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-800 dark:text-green-200">
              {payments.filter(p => p.status === 'completed').length}
            </div>
            <p className="text-xs text-green-600 dark:text-green-400">
              {formatCurrency(payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0))} total
            </p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
              {payments.filter(p => p.status === 'pending').length}
            </div>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              {formatCurrency(payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0))} pending
            </p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-700 dark:text-red-300">Failed</CardTitle>
            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-800 dark:text-red-200">
              {payments.filter(p => p.status === 'failed').length}
            </div>
            <p className="text-xs text-red-600 dark:text-red-400">
              {formatCurrency(payments.filter(p => p.status === 'failed').reduce((sum, p) => sum + p.amount, 0))} failed
            </p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Net Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
              {formatCurrency(payments.reduce((sum, p) => sum + p.netAmount, 0))}
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400">After fees & refunds</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="relative w-full md:w-1/3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            type="text"
            placeholder="Search payments..."
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            className="pl-9 pr-3 py-2 border rounded-md w-full"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
          <select
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Methods</option>
            <option value="credit_card">Credit Card</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="paypal">PayPal</option>
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 border rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="oldest">Oldest First</option>
            <option value="amount-high">Amount (High to Low)</option>
            <option value="amount-low">Amount (Low to High)</option>
          </select>
        </div>
      </div>

      {/* Payments List */}
      <div className="space-y-4">
        {filteredPayments.length === 0 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Payments Found</AlertTitle>
            <AlertDescription>
              No payments match your current search and filter criteria. Try adjusting your filters.
            </AlertDescription>
          </Alert>
        ) : (
          filteredPayments.map(payment => (
            <Card key={payment.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{payment.transactionId}</h3>
                      {getStatusBadge(payment.status)}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Customer:</span> {payment.customer} ({payment.customerEmail})
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div>
                        <span className="font-medium">Amount:</span> {formatCurrency(payment.amount)}
                      </div>
                      <div>
                        <span className="font-medium">Method:</span> 
                        <span className="flex items-center gap-1 ml-1">
                          {getPaymentMethodIcon(payment.paymentMethod)}
                          {payment.paymentMethod.replace('_', ' ')}
                          {payment.cardLast4 && (
                            <span className={`font-medium ${getCardBrandColor(payment.cardBrand)}`}>
                              •••• {payment.cardLast4}
                            </span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium">Invoice:</span> {payment.invoiceId}
                      </div>
                      <div>
                        <span className="font-medium">Subscription:</span> {payment.subscription}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <div>
                        <span className="font-medium">Created:</span> {formatDateTime(payment.createdAt)}
                      </div>
                      {payment.processedAt && (
                        <div>
                          <span className="font-medium">Processed:</span> {formatDateTime(payment.processedAt)}
                        </div>
                      )}
                      {payment.processingFee > 0 && (
                        <div>
                          <span className="font-medium">Fee:</span> {formatCurrency(payment.processingFee)}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Gateway:</span> {payment.gateway}
                      </div>
                    </div>
                    {payment.failureReason && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                        <p className="text-sm text-red-800 dark:text-red-200">
                          <span className="font-medium">Failure Reason:</span> {payment.failureReason}
                        </p>
                      </div>
                    )}
                    {payment.refundAmount > 0 && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-md">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          <span className="font-medium">Refund:</span> {formatCurrency(payment.refundAmount)} - {payment.refundReason}
                          {payment.refundedAt && (
                            <span className="ml-2">({formatDate(payment.refundedAt)})</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 md:mt-0 flex flex-col items-end space-y-2">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {formatCurrency(payment.netAmount)}
                      </div>
                      {payment.processingFee > 0 && (
                        <div className="text-sm text-gray-500">
                          Fee: {formatCurrency(payment.processingFee)}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" /> View
                      </Button>
                      {payment.status === 'failed' && (
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => handleRetryPayment(payment.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" /> Retry
                        </Button>
                      )}
                      {payment.status === 'completed' && payment.refundAmount === 0 && (
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={() => handleRefundPayment(payment.id)}
                        >
                          <DollarSign className="h-4 w-4 mr-1" /> Refund
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
